import { useQuery } from '@tanstack/react-query';
import { gql, request } from 'graphql-request';

import { GraphQLEndpoint } from './endpoint';
import { Country } from './types/graphql';

export const query = gql`
  query Countries($countryCode: String) {
    content {
      countries(countryCode: $countryCode) {
        currency
        currencyForSubscription
        supportedCurrencies
        status
        membershipStatus
        countryCode
        shippingRate {
          description
          price
        }
        eu
        name
        regions {
          label
          value
          labelMsgKey
        }
        merchant {
          prod
          staging
        }
        disallowedProductSkus
        labelMsgKey
        enableForRma
        showNoAdditionalTaxesMessage
      }
    }
  }
`;

interface ContentResponse {
  content: {
    countries: Country[];
  };
}

function withQueryKey(vars: { countryCode?: string } = {}) {
  return {
    key: ['Countries', { vars }],
    fetch: async () => {
      const data: ContentResponse = await request(GraphQLEndpoint, query, vars);

      const byCountryCode = new Map();

      for (const country of data?.content?.countries || []) {
        byCountryCode.set(country?.countryCode, country);
      }

      return {
        countries: data?.content?.countries,
        byCountryCode,
      };
    },
  };
}

export function useCountry(countryCode: string): {
  isError: boolean;
  isLoading: boolean;
  data: Country;
} {
  const { key, fetch } = withQueryKey({ countryCode });
  const { data, isLoading, isError } = useQuery({
    queryKey: key,
    queryFn: fetch,
  });

  return {
    isLoading,
    isError,
    data: data?.byCountryCode.get(countryCode),
  };
}

/**
 * Fetches list of all countries with `status: true`
 * Optionally return all countries with `ignoreStatus`
 */

export function useCountriesList(
  options: {
    ignoreStatus?: boolean;
  } = {},
): {
  isError: boolean;
  isLoading: boolean;
  data: Country[];
} {
  const { ignoreStatus } = options;
  const { key, fetch } = withQueryKey();
  const { data, isError, isLoading } = useQuery({
    queryKey: key,
    queryFn: fetch,
  });

  const dataCountries = data?.countries ?? [];
  const filteredData = dataCountries
    .filter((country) => country !== null)
    .filter((country: Country): boolean => {
      if (ignoreStatus) return true;
      return Boolean(country.status);
    });

  return {
    isError,
    isLoading,
    data: filteredData,
  };
}
